import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Library, Mail, Lock, ArrowRight, X, Loader2, CheckCircle2, UserCircle2 } from 'lucide-react';
import ForgotPassword from '../api/ForgotPassword';
import ResetPassword from '../api/ResetPassword';
import GetEtablissements from '../api/GetEtablissements';
import { useSelector } from 'react-redux';

export default function Login({ onLogin, onGuestLogin }) {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isGuestMode, setIsGuestMode] = useState(false);
    const [etablissements, setEtablissements] = useState([]);
    const [selectedEtablissement, setSelectedEtablissement] = useState('');
    const [isLoadingEtablissements, setIsLoadingEtablissements] = useState(false);
    const API_BASE = useSelector((state) => state.user.apiBase);
    const [isForgotPasswordOpen, setIsForgotPasswordOpen] = useState(false);
    const [isResetPasswordOpen, setIsResetPasswordOpen] = useState(false);
    const [resetToken, setResetToken] = useState('');

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const token = params.get('resetpassword');
        if (token) {
            setResetToken(token);
            setIsResetPasswordOpen(true);
            const newUrl = window.location.pathname;
            window.history.replaceState({}, document.title, newUrl);
        }
    }, []);

    useEffect(() => {
        if (isGuestMode && etablissements.length === 0) {
            setIsLoadingEtablissements(true);
            GetEtablissements(API_BASE)
                .then(data => {
                    setEtablissements(data);
                    //if (data.length > 0) setSelectedEtablissement(data[0].code);
                })
                .catch(err => console.error("Error fetching establishments:", err))
                .finally(() => setIsLoadingEtablissements(false));
        }
    }, [isGuestMode, API_BASE, etablissements.length]);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (isGuestMode) {
            if (selectedEtablissement) {
                onGuestLogin(selectedEtablissement);
            }
        } else {
            if (email && password) {
                onLogin(email, password);
            }
        }
    };

    return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
            {/* Background Gradients */}
            <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
                <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-primary/10 rounded-full blur-[120px]" />
                <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-purple-500/10 rounded-full blur-[120px]" />
            </div>

            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="w-full max-w-md relative z-10"
            >
                {/* Logo & Title */}
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/20 rounded-2xl border border-primary/20 mb-4">
                        <Library className="text-primary h-8 w-8" />
                    </div>
                    <h1 className="text-3xl font-bold bg-gradient-to-r from-foreground to-muted-foreground bg-clip-text text-transparent mb-2">
                        Biblius
                    </h1>
                    <p className="text-muted-foreground">Votre bibliothèque</p>
                </div>

                {/* Login Card */}
                <div className="bg-card/50 backdrop-blur-xl border border-border rounded-2xl p-8 shadow-2xl">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-2xl font-bold text-foreground">
                            {isGuestMode ? "Accès Invité" : "Connexion"}
                        </h2>
                        <button
                            type="button"
                            onClick={() => setIsGuestMode(!isGuestMode)}
                            className="text-sm text-primary hover:underline font-medium"
                        >
                            {isGuestMode ? "Retour connexion" : "Accès Invité ?"}
                        </button>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-5">
                        {!isGuestMode ? (
                            <>
                                {/* Email Field */}
                                <div>
                                    <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
                                        Email
                                    </label>
                                    <div className="relative">
                                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-5 w-5" />
                                        <input
                                            id="email"
                                            type="email"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            placeholder="exemple@biblius.fr"
                                            className="w-full bg-muted/50 border border-border rounded-xl py-3 pl-11 pr-4 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
                                            required
                                        />
                                    </div>
                                </div>

                                {/* Password Field */}
                                <div>
                                    <label htmlFor="password" className="block text-sm font-medium text-foreground mb-2">
                                        Mot de passe
                                    </label>
                                    <div className="relative">
                                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-5 w-5" />
                                        <input
                                            id="password"
                                            type="password"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            placeholder="••••••••"
                                            className="w-full bg-muted/50 border border-border rounded-xl py-3 pl-11 pr-4 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
                                            required
                                        />
                                    </div>
                                </div>

                                {/* Remember & Forgot */}
                                <div className="flex items-center justify-between text-sm">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            className="w-4 h-4 rounded border-border bg-muted text-primary focus:ring-2 focus:ring-primary/50"
                                        />
                                        <span className="text-muted-foreground">Se souvenir de moi</span>
                                    </label>
                                    <button
                                        type="button"
                                        onClick={() => setIsForgotPasswordOpen(true)}
                                        className="text-primary hover:underline"
                                    >
                                        Mot de passe oublié ?
                                    </button>
                                </div>
                            </>
                        ) : (
                            <>
                                {/* Establishment Selection */}
                                <div>
                                    <label htmlFor="etablissement" className="block text-sm font-medium text-foreground mb-2">
                                        Sélectionner votre établissement
                                    </label>
                                    <div className="relative">
                                        <UserCircle2 className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-5 w-5" />
                                        <select
                                            id="etablissement"
                                            value={selectedEtablissement}
                                            onChange={(e) => setSelectedEtablissement(e.target.value)}
                                            className="w-full bg-muted/50 border border-border rounded-xl py-3 pl-11 pr-4 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all appearance-none"
                                            disabled={isLoadingEtablissements}
                                            required
                                        >
                                            <option value="" disabled>Choisissez votre établissement</option>
                                            {isLoadingEtablissements ? (
                                                <option>Chargement...</option>
                                            ) : (
                                                etablissements.map((etab) => (
                                                    <option key={etab.code_etablissement} value={etab.code_etablissement}>
                                                        {etab.name}
                                                    </option>
                                                ))
                                            )}
                                        </select>
                                    </div>
                                </div>
                            </>
                        )}

                        {/* Submit Button */}
                        <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            type="submit"
                            disabled={isGuestMode && (isLoadingEtablissements || etablissements.length === 0 || !selectedEtablissement)}
                            className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-medium py-3 px-4 rounded-xl transition-colors flex items-center justify-center gap-2 group disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isGuestMode ? "Accès Invité" : "Se connecter"}
                            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                        </motion.button>
                    </form>

                    {/* Footer */}
                    <div className="mt-6 pt-6 border-t border-border text-center">
                        <p className="text-sm text-muted-foreground">
                            Vous n'avez pas de compte ?{' '}
                            <a href="#" className="text-primary hover:underline font-medium">
                                Contactez l'administrateur
                            </a>
                        </p>
                    </div>
                </div>

                {/* Version Info */}
                <div className="text-center mt-8 text-sm text-muted-foreground">
                    Biblius v1.0 - © 2025
                </div>
            </motion.div>

            <ForgotPasswordModal
                isOpen={isForgotPasswordOpen}
                onClose={() => setIsForgotPasswordOpen(false)}
            />

            <ResetPasswordModal
                isOpen={isResetPasswordOpen}
                onClose={() => setIsResetPasswordOpen(false)}
                token={resetToken}
            />
        </div>
    );
}

const ResetPasswordModal = ({ isOpen, onClose, token }) => {
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [error, setError] = useState(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (password !== confirmPassword) {
            setError("Les mots de passe ne correspondent pas.");
            return;
        }

        setIsLoading(true);
        setError(null);
        try {
            await ResetPassword(token, password);
            setIsSuccess(true);
            setTimeout(() => {
                onClose();
            }, 3000);
        } catch (err) {
            setError(err.message || "Erreur lors de la réinitialisation.");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 20 }}
                        className="bg-card border border-border rounded-2xl p-8 w-full max-w-md shadow-2xl relative"
                    >
                        {!isSuccess && (
                            <button
                                onClick={onClose}
                                className="absolute top-4 right-4 text-muted-foreground hover:text-foreground p-2"
                            >
                                <X size={20} />
                            </button>
                        )}

                        <div className="text-center mb-6">
                            <div className={`inline-flex items-center justify-center w-12 h-12 ${isSuccess ? 'bg-emerald-500/20 border-emerald-500/20' : 'bg-primary/20 border-primary/20'} rounded-xl border mb-4`}>
                                {isSuccess ? <CheckCircle2 className="text-emerald-500 h-6 w-6" /> : <Lock className="text-primary h-6 w-6" />}
                            </div>
                            <h3 className="text-xl font-bold text-foreground">
                                {isSuccess ? "Succès !" : "Nouveau mot de passe"}
                            </h3>
                            <p className="text-muted-foreground text-sm mt-2">
                                {isSuccess
                                    ? "Votre mot de passe a été modifié avec succès. Vous allez être redirigé vers la page de connexion."
                                    : "Veuillez saisir votre nouveau mot de passe ci-dessous."}
                            </p>
                            {error && (
                                <p className="text-red-500 text-sm mt-2 bg-red-500/10 py-2 rounded-lg border border-red-500/20">
                                    {error}
                                </p>
                            )}
                        </div>

                        {!isSuccess && (
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-foreground mb-2">
                                        Nouveau mot de passe
                                    </label>
                                    <div className="relative">
                                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-5 w-5" />
                                        <input
                                            type="password"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            placeholder="••••••••"
                                            className="w-full bg-muted/50 border border-border rounded-xl py-3 pl-11 pr-4 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all"
                                            required
                                            minLength={6}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-foreground mb-2">
                                        Confirmer le mot de passe
                                    </label>
                                    <div className="relative">
                                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-5 w-5" />
                                        <input
                                            type="password"
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            placeholder="••••••••"
                                            className="w-full bg-muted/50 border border-border rounded-xl py-3 pl-11 pr-4 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all"
                                            required
                                            minLength={6}
                                        />
                                    </div>
                                </div>

                                <motion.button
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    disabled={isLoading}
                                    type="submit"
                                    className={`w-full bg-primary hover:bg-primary/90 text-primary-foreground font-medium py-3 px-4 rounded-xl transition-colors flex items-center justify-center gap-2 ${isLoading ? 'opacity-70 cursor-not-allowed' : ''}`}
                                >
                                    {isLoading ? (
                                        <>
                                            <Loader2 className="animate-spin h-5 w-5" />
                                            Enregistrement...
                                        </>
                                    ) : (
                                        "Changer le mot de passe"
                                    )}
                                </motion.button>
                            </form>
                        )}
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
};

const ForgotPasswordModal = ({ isOpen, onClose }) => {
    const [email, setEmail] = useState('');
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError(null);
        try {
            await ForgotPassword(email);
            setIsSubmitted(true);
        } catch (err) {
            setError("Compte inexistant");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 20 }}
                        className="bg-card border border-border rounded-2xl p-8 w-full max-w-md shadow-2xl relative"
                    >
                        <button
                            onClick={onClose}
                            className="absolute top-4 right-4 text-muted-foreground hover:text-foreground p-2"
                        >
                            <X size={20} />
                        </button>

                        <div className="text-center mb-6">
                            <div className="inline-flex items-center justify-center w-12 h-12 bg-primary/20 rounded-xl border border-primary/20 mb-4">
                                <Mail className="text-primary h-6 w-6" />
                            </div>
                            <h3 className="text-xl font-bold text-foreground">Réinitialiser le mot de passe</h3>
                            <p className="text-muted-foreground text-sm mt-2">
                                {isSubmitted
                                    ? "Si un compte existe pour cet email, vous recevrez un lien de réinitialisation."
                                    : "Entrez votre email pour recevoir les instructions de réinitialisation."}
                            </p>
                            {error && (
                                <p className="text-red-500 text-sm mt-2 bg-red-500/10 py-2 rounded-lg border border-red-500/20">
                                    {error}
                                </p>
                            )}
                        </div>

                        {!isSubmitted ? (
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div>
                                    <label htmlFor="reset-email" className="block text-sm font-medium text-foreground mb-2">
                                        Email
                                    </label>
                                    <div className="relative">
                                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-5 w-5" />
                                        <input
                                            id="reset-email"
                                            type="email"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            placeholder="exemple@biblius.fr"
                                            className="w-full bg-muted/50 border border-border rounded-xl py-3 pl-11 pr-4 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
                                            required
                                        />
                                    </div>
                                </div>

                                <motion.button
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    disabled={isLoading}
                                    type="submit"
                                    className={`w-full bg-primary hover:bg-primary/90 text-primary-foreground font-medium py-3 px-4 rounded-xl transition-colors flex items-center justify-center gap-2 ${isLoading ? 'opacity-70 cursor-not-allowed' : ''}`}
                                >
                                    {isLoading ? (
                                        <>
                                            <Loader2 className="animate-spin h-5 w-5" />
                                            Envoi en cours...
                                        </>
                                    ) : (
                                        "Envoyer le lien"
                                    )}
                                </motion.button>
                            </form>
                        ) : (
                            <motion.button
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                onClick={onClose}
                                className="w-full bg-muted hover:bg-muted/80 text-foreground font-medium py-3 px-4 rounded-xl transition-colors mt-4"
                            >
                                Fermer
                            </motion.button>
                        )}
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
};
